/**
 * Извлечение plain text из блоков CMS-страницы для клиентского поиска.
 * Глубже бэкенд-PagePlainTextExtractor: рекурсия в sections/cells/items.
 */

const TEXT_KEYS = new Set([
  'title', 'subtitle', 'heading', 'text', 'content', 'description',
  'buttontext', 'buttonlabel', 'label', 'caption', 'alt', 'name',
  'body', 'html', 'richtext', 'morelabel',
]);

const TEXTISH_PART = /title|text|content|description|label|heading/i;

const SKIP_KEYS = new Set([
  'id', 'type', 'order', 'image', 'src', 'url', 'href', 'link', 'to', 'path',
  'icon', 'color', 'class', 'className', 'style', 'width', 'height',
  'previewToken', 'media', 'slug', 'anchor',
]);

const MAX_PAGE_CHARS = 12000;
const blocksTextCache = new Map();

function isTextishKey(key) {
  if (key == null || key === '') return false;
  const k = String(key).toLowerCase();
  if (SKIP_KEYS.has(k)) return false;
  if (TEXT_KEYS.has(k)) return true;
  return TEXTISH_PART.test(k);
}

function looksLikeUrlOrId(s) {
  const t = s.trim();
  if (!t) return true;
  if (/^https?:\/\//i.test(t)) return true;
  if (/^\/(api|images|documents|media|static)\b/i.test(t)) return true;
  if (/^data:/i.test(t)) return true;
  if (/^[0-9a-f]{24}$/i.test(t)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(t)) return true;
  return false;
}

function looksLikeProse(s) {
  if (!s || looksLikeUrlOrId(s)) return false;
  const t = s.trim();
  if (t.length < 2) return false;
  // Cyrillic / Latin words or multi-word / HTML
  if (/[а-яёА-ЯЁ]/.test(t)) return true;
  if (/\s/.test(t) && /[a-zA-Z]{2,}/.test(t)) return true;
  if (/<[a-z][\s\S]*>/i.test(t)) return true;
  return t.length >= 4 && /[a-zA-Zа-яёА-ЯЁ]/.test(t);
}

export function htmlToText(raw) {
  if (raw == null) return '';
  const s = String(raw);
  if (!s) return '';
  let plain;
  if (typeof document !== 'undefined') {
    try {
      const div = document.createElement('div');
      div.innerHTML = s;
      plain = div.textContent || div.innerText || '';
    } catch (e) {
      plain = s.replace(/<[^>]+>/g, ' ');
    }
  } else {
    plain = s.replace(/<[^>]+>/g, ' ');
  }
  return normalizeWhitespace(plain);
}

function normalizeWhitespace(s) {
  if (!s) return '';
  return String(s)
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t\f\r]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function appendString(parts, raw, key) {
  if (raw == null || raw === '') return;
  const asStr = String(raw);
  const k = key != null ? String(key).toLowerCase() : '';
  const needsHtml = /</.test(asStr) || k === 'content' || k === 'html' || k === 'richtext' || k === 'body';
  const plain = needsHtml ? htmlToText(asStr) : normalizeWhitespace(asStr);
  if (!plain) return;
  parts.push(plain);
}

/**
 * Рекурсивно собирает текст из произвольного значения data-блока.
 * @param {unknown} value
 * @param {string|null} key
 * @param {string[]} parts
 * @param {number} depth
 */
function collectValue(value, key, parts, depth) {
  if (value == null || depth > 12) return;

  if (typeof value === 'string') {
    if (looksLikeUrlOrId(value)) return;
    // В массивах ключ родителя (sections/items/cells) часто не textish —
    // берём строки, похожие на prose, либо если ключ textish.
    if (isTextishKey(key) || looksLikeProse(value)) {
      appendString(parts, value, key);
    }
    return;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return;
  }

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      collectValue(value[i], key, parts, depth + 1);
    }
    return;
  }

  if (typeof value === 'object') {
    // nested block shape { type, data }
    if (value.data && typeof value.data === 'object') {
      collectValue(value.data, 'data', parts, depth + 1);
    }
    Object.keys(value).forEach((k) => {
      if (k === 'data' && value.data && typeof value.data === 'object') {
        return;
      }
      if (SKIP_KEYS.has(String(k).toLowerCase()) && !isTextishKey(k)) {
        // всё равно заходим в контейнеры sections/cells/items/rows/blocks
        const lower = String(k).toLowerCase();
        if (
          lower === 'sections'
          || lower === 'cells'
          || lower === 'items'
          || lower === 'rows'
          || lower === 'blocks'
          || lower === 'children'
          || lower === 'columns'
          || lower === 'nested'
        ) {
          collectValue(value[k], k, parts, depth + 1);
        }
        return;
      }
      collectValue(value[k], k, parts, depth + 1);
    });
  }
}

/**
 * @param {Array<{ data?: object }>|null|undefined} blocks
 * @returns {string} plain text (original case, normalized)
 */
export function extractBlocksPlainText(blocks) {
  if (!blocks || !blocks.length) return '';
  const parts = [];
  const sorted = blocks.slice().sort((a, b) => {
    const ao = a && a.order != null ? a.order : Number.MAX_SAFE_INTEGER;
    const bo = b && b.order != null ? b.order : Number.MAX_SAFE_INTEGER;
    return ao - bo;
  });
  for (let i = 0; i < sorted.length; i += 1) {
    const block = sorted[i];
    if (!block || !block.data) continue;
    collectValue(block.data, null, parts, 0);
  }
  let text = normalizeWhitespace(parts.join('\n'));
  if (text.length > MAX_PAGE_CHARS) {
    text = text.slice(0, MAX_PAGE_CHARS);
  }
  return text;
}

/**
 * Plain text блоков страницы с кешем по id+updatedAt.
 * @param {{ id?: string, slug?: string, updatedAt?: string, blocks?: Array }} page
 * @returns {{ plain: string, lower: string }}
 */
export function getCachedBlocksPlain(page) {
  if (!page) return { plain: '', lower: '' };
  const cacheKey = `${page.id || page.slug || ''}|${page.updatedAt || ''}`;
  if (cacheKey !== '|' && blocksTextCache.has(cacheKey)) {
    return blocksTextCache.get(cacheKey);
  }
  const plain = extractBlocksPlainText(page.blocks);
  const entry = { plain, lower: plain.toLowerCase() };
  if (cacheKey !== '|') {
    if (blocksTextCache.size > 200) {
      const first = blocksTextCache.keys().next().value;
      blocksTextCache.delete(first);
    }
    blocksTextCache.set(cacheKey, entry);
  }
  return entry;
}

/**
 * Lowercase plain text блоков (удобная обёртка для фильтра поиска).
 * @param {{ id?: string, updatedAt?: string, blocks?: Array }} page
 * @returns {string}
 */
export function getCachedBlocksText(page) {
  return getCachedBlocksPlain(page).lower;
}

/**
 * Короткий excerpt вокруг первого вхождения query в plain text.
 * @param {string} plainText original or lower — ищем по lower, возвращаем из original
 * @param {string} queryLower
 * @param {number} [radius=60]
 */
export function makeExcerpt(plainText, queryLower, radius = 60) {
  if (!plainText || !queryLower) return '';
  const lower = plainText.toLowerCase();
  const idx = lower.indexOf(queryLower);
  if (idx < 0) return '';
  const start = Math.max(0, idx - radius);
  const end = Math.min(plainText.length, idx + queryLower.length + radius);
  let snippet = plainText.slice(start, end).replace(/\s+/g, ' ').trim();
  if (start > 0) snippet = `…${snippet}`;
  if (end < plainText.length) snippet = `${snippet}…`;
  return snippet;
}
